<script lang="ts">
    import { base } from '$app/paths';
    import { onMount, createEventDispatcher } from 'svelte';
    import { setPdfContext } from '../context/PdfContext';
    import { createDocumentStore } from '../stores/documentStore';
    import { createNavigationStore } from '../stores/navigationStore';
    import PdfDocument from './PdfDocument.svelte';
    import type { PdfSource, PdfContext, DocumentStore, NavigationStore } from '../types';

    // Props
    export let contract: PdfSource | null | undefined;
    export let enableRangeRequests: boolean = true;
    export let chunkSize: number = 131072; // 128KB

    // Events
    const dispatch = createEventDispatcher<{
        pagechange: { page: number };
        load: { totalPages: number };
        error: { error: Error };
        storesready: {
            documentStore: DocumentStore;
            navigationStore: NavigationStore;
            scrollToPage: PdfContext['scrollToPage'];
        };
    }>();

    // Stores 생성
    const documentStore = createDocumentStore();
    const navigationStore = createNavigationStore();

    // Context 제공
    const context: PdfContext = {
        documentStore,
        navigationStore,
        scrollToPage: (uiIndex: number) => {
            const docPage = navigationStore.uiIndexToDocumentPage(uiIndex);
            if (!docPage) return;

            // DOM 조작은 컴포넌트에서 처리
            const event = new CustomEvent('scrolltopage', { detail: { uiIndex, docPage } });
            window.dispatchEvent(event);
        },
    };

    setPdfContext(context);

    const notifyStoresReady = (): void => {
        dispatch('storesready', { documentStore, navigationStore, scrollToPage: context.scrollToPage });
    };

    let mounted = false;
    let currentSource: PdfSource | null = null;
    let loadToken = 0;

    const resetState = (): void => {
        navigationStore.reset();
        documentStore.destroy();
    };

    const loadContractSource = async (source: PdfSource): Promise<void> => {
        resetState();
        const token = ++loadToken;

        try {
            await documentStore.loadDocument(source, {
                cMapUrl: `${base}/pdfjs/cmaps/`,
                enableRangeRequests,
                chunkSize,
            });

            if (token !== loadToken) {
                return;
            }

            if ($documentStore.document) {
                dispatch('load', { totalPages: $documentStore.totalPages });
            }
        } catch (error) {
            if (token !== loadToken) {
                return;
            }

            const err = error instanceof Error ? error : new Error('Failed to load PDF');
            dispatch('error', { error: err });
        }
    };

    // 현재 페이지 변경 감지 및 이벤트 발생
    $: if ($navigationStore.currentPage) {
        dispatch('pagechange', { page: $navigationStore.currentPage });
    }

    onMount(() => {
        mounted = true;
        notifyStoresReady();

        if (contract) {
            currentSource = contract;
            loadContractSource(contract);
        }

        return () => {
            mounted = false;
            resetState();
        };
    });

    $: if (mounted) {
        if (contract && contract !== currentSource) {
            currentSource = contract;
            loadContractSource(contract);
        } else if (!contract && currentSource) {
            currentSource = null;
            resetState();
        }
    }
</script>

{#if $documentStore.loading}
    <slot name="loading">
        <div class="flex items-center justify-center p-8">
            <span>Loading PDF...</span>
        </div>
    </slot>
{:else if $documentStore.error}
    <slot name="error" error={$documentStore.error}>
        <div class="flex items-center justify-center p-8 text-red-600">
            <span>Error: {$documentStore.error.message}</span>
        </div>
    </slot>
{:else if $documentStore.document}
    <div class="relative h-full w-full">
        <slot {documentStore} {navigationStore}>
            <PdfDocument pageClass="shadow" />
        </slot>
    </div>
{/if}
